import React, { useState } from 'react';
import { api, apiPost } from '../lib/api';
import { formatDate as _fmtD } from '../lib/format';
import { toast } from 'sonner';
import {
  X, Upload, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Loader2, Eye, FileSpreadsheet,
} from 'lucide-react';

/**
 * Iter 252 / Fork 51 / Change 2 — Universal mapped BOQ upload wizard.
 *
 *   Step 1 (Upload)  POST /boq/analyze-excel   → suggested mapping per sheet
 *   Step 2 (Map)     user reviews / tweaks mapping; "Apply Saved Mapping" if any
 *   Step 3 (Confirm) POST /boq/import-mapped (dry_run=true) → preview;
 *                    then POST /boq/import-mapped (dry_run=false) on Confirm.
 *
 *  Props: projectId, hasExistingBoq, onClose, onImported
 */
const CONCEPTS = [
  { key: 'item_code',  label: 'Item Code',   required: false },
  { key: 'description', label: 'Description', required: true },
  { key: 'unit',       label: 'Unit',        required: false },
  { key: 'qty',        label: 'Quantity',    required: false },
  { key: 'rate',       label: 'Rate',        required: false },
  { key: 'amount',     label: 'Amount',      required: false },
];

const CONF_BADGE = {
  high:   { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'High match' },
  medium: { cls: 'bg-amber-50 text-amber-700 border-amber-200',       label: 'Medium' },
  low:    { cls: 'bg-rose-50 text-rose-700 border-rose-200',         label: 'Low — please review' },
};

const colLetter = (n) => {
  // Excel-style col index → letter ('A' = 1)
  let s = '';
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s || '—';
};

const inr = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

export default function BOQUploadWizard({ projectId, hasExistingBoq, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);   // server response
  const [sheetCfg, setSheetCfg] = useState([]);     // editable sheets[] for step 2/3
  const [replace, setReplace] = useState(hasExistingBoq);
  const [preview, setPreview] = useState(null);     // dry_run response
  const [busy, setBusy] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);

  // ── step 1 — upload + analyse ───────────────────────────────────────────
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post(`/boq/analyze-excel?project_id=${projectId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnalysis(r.data);
      setSheetCfg(r.data.sheets.map((s) => ({
        name: s.name,
        include: !s.skip_suggested,
        skip_suggested: !!s.skip_suggested,
        might_be_summary: !!s.might_be_summary,
        header_row: s.suggested_header_row || 1,
        mapping: { ...s.suggested_mapping },
        section_title: s.name,
        confidence: s.confidence,
        preview: s.preview,
        rows: s.rows,
        cols: s.cols,
        detected_columns: s.detected_columns || [],
        // keep originals so 'Reset' can restore them.
        _suggested_header_row: s.suggested_header_row || 1,
        _suggested_mapping: { ...s.suggested_mapping },
        _detected_columns: s.detected_columns || [],
      })));
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not analyse file');
    } finally {
      setBusy(false);
    }
  };

  const applySavedMapping = () => {
    if (!analysis?.saved_mapping?.sheets) return;
    const saved = analysis.saved_mapping.sheets;
    setSheetCfg((cfg) => cfg.map((c) => {
      const m = saved.find((s) => s.name === c.name);
      if (!m) return c;
      let include = m.include;
      // Never silently re-include a sheet the fresh analysis says to skip.
      if (include && c.skip_suggested && !c.include) {
        include = window.confirm(
          `Saved mapping includes '${c.name}', which the current analysis suggests skipping (summary/no header detected). Include it anyway?`
        );
      }
      return {
        ...c,
        include,
        header_row: m.header_row,
        mapping: { ...m.mapping },
        section_title: m.section_title || c.section_title,
      };
    }));
    toast.success('Applied saved mapping from last import');
  };

  // ── step 2 → step 3 (dry-run preview) ──────────────────────────────────
  const goPreview = async (cfgOverride) => {
    const cfg = cfgOverride || sheetCfg;
    const included = cfg.filter((s) => s.include);
    if (!included.length) { toast.error('Include at least one sheet'); return; }
    for (const s of included) {
      if (!s.mapping?.description) {
        toast.error(`Sheet "${s.name}": Description column is required`);
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        upload_token: analysis.upload_token,
        project_id: projectId,
        replace,
        dry_run: true,
        sheets: cfg.map((s) => ({
          name: s.name,
          include: s.include,
          header_row: s.header_row,
          mapping: s.mapping,
          section_title: s.section_title || s.name,
        })),
      };
      const r = await apiPost('/boq/import-mapped', body);
      setPreview(r);
      setStep(3);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) {
        toast.error('Session expired — please re-upload your BOQ file');
        setStep(1);
        setAnalysis(null);
        setFile(null);
      } else {
        const raw = err?.response?.data?.detail;
        const msg = typeof raw === 'string' ? raw
          : Array.isArray(raw) ? raw.map((e) => e?.msg || JSON.stringify(e)).join('; ')
          : 'Preview failed';
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  // ── step 3 — commit ────────────────────────────────────────────────────
  const commit = async () => {
    if (replace && !confirmReplace) {
      toast.error('Tick "I understand the existing BOQ will be replaced" first');
      return;
    }
    setBusy(true);
    try {
      const body = {
        upload_token: analysis.upload_token,
        project_id: projectId,
        replace,
        dry_run: false,
        sheets: sheetCfg.map((s) => ({
          name: s.name, include: s.include, header_row: s.header_row,
          mapping: s.mapping, section_title: s.section_title || s.name,
        })),
      };
      const r = await apiPost('/boq/import-mapped', body);
      toast.success(`Imported ${r.imported} BOQ items · ₹${Math.round((r.total_value || 0) / 1e7).toLocaleString('en-IN')} Cr total`);
      onImported?.(r);
      onClose?.();
    } catch (err) {
      const rawE = err?.response?.data?.detail;
      toast.error(typeof rawE === 'string' ? rawE : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const updateSheet = (idx, patch) =>
    setSheetCfg((cfg) => cfg.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const excludeSheetAndRepreview = (sheetName) => {
    const next = sheetCfg.map((s) => (s.name === sheetName ? { ...s, include: false } : s));
    setSheetCfg(next);
    goPreview(next);
  };
  const updateSheetMapping = (sheetName, colIndex, concept) => {
    setSheetCfg((cfg) => cfg.map((s) => {
      if (s.name !== sheetName) return s;
      const newMapping = Object.fromEntries(
        Object.entries(s.mapping || {}).filter(([, v]) => v !== colIndex)
      );
      if (concept !== '__ignore__') newMapping[concept] = colIndex;
      return { ...s, mapping: newMapping };
    }));
  };

  const updateMapping = (idx, concept, col) =>
    setSheetCfg((cfg) => cfg.map((s, i) => {
      if (i !== idx) return s;
      const m = { ...s.mapping };
      if (col === '' || col === null) delete m[concept];
      else m[concept] = parseInt(col);
      return { ...s, mapping: m };
    }));

  // ── render ──────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="boq-upload-wizard">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="border-b px-5 py-3 flex items-center justify-between bg-[#3b4872] text-white">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} />
            <h2 className="font-display font-bold tracking-wide uppercase text-sm">Smart BOQ Upload</h2>
            <span className="ml-3 text-[11px] text-white/75">Step {step} of 3</span>
          </div>
          <button onClick={onClose} className="hover:opacity-80" data-testid="boq-wizard-close"><X size={20} /></button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 px-5 py-2 border-b bg-slate-50 text-xs">
          {['Upload file', 'Confirm mapping', 'Preview & confirm'].map((label, i) => {
            const n = i + 1;
            const active = step === n, done = step > n;
            return (
              <React.Fragment key={n}>
                <span className={`px-2 py-1 rounded-sm ${active ? 'bg-indigo-100 text-indigo-800 font-semibold' : done ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500'}`}>
                  {done ? <CheckCircle2 size={11} className="inline mb-0.5" /> : `${n}.`} {label}
                </span>
                {n < 3 && <ArrowRight size={11} className="text-slate-400" />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="overflow-auto flex-1 p-5">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="text-center py-10" data-testid="wizard-step-upload">
              <Upload size={36} className="mx-auto text-slate-300 mb-4" />
              <h3 className="font-display font-bold text-lg text-slate-800 mb-2">Upload your client&apos;s BOQ</h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto mb-6">
                Any .xlsx layout works. We&apos;ll detect the header row and map columns automatically.
                You&apos;ll review and tweak the mapping in the next step.
              </p>
              <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] font-display font-semibold text-sm cursor-pointer">
                <input type="file" accept=".xlsx,.xls,.xlsm" onChange={onPickFile}
                       data-testid="wizard-file-input" className="hidden" disabled={busy} />
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {busy ? 'Analysing…' : 'Choose Excel file'}
              </label>
              {file && (
                <p className="text-xs text-slate-500 mt-3">
                  Selected: <b>{file.name}</b> ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && analysis && (
            <div data-testid="wizard-step-mapping">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-base text-slate-800">
                  Confirm column mapping for {analysis.sheets.length} sheet(s)
                </h3>
                {analysis.saved_mapping && (
                  <button onClick={applySavedMapping} data-testid="wizard-apply-saved-mapping"
                          className="text-xs px-3 py-1.5 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                    Use saved mapping from {_fmtD(analysis.saved_mapping.saved_at)}
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {sheetCfg.map((s, idx) => (
                  <div key={s.name} className="border rounded-sm" data-testid={`wizard-sheet-${idx}`}>
                    <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between">
                      <label className="inline-flex items-center gap-2 text-sm font-semibold">
                        <input type="checkbox" className="accent-indigo-600" checked={s.include}
                               onChange={(e) => updateSheet(idx, { include: e.target.checked })}
                               data-testid={`wizard-sheet-include-${idx}`} />
                        <span>{s.name}</span>
                        <span className="text-[11px] text-slate-500">{s.rows}×{s.cols}</span>
                        {s.skip_suggested && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-300"
                                data-testid={`wizard-skip-chip-${idx}`}>
                            Suggested: skip{s.might_be_summary ? ' (summary/cover sheet)' : ' (no header detected)'}
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${CONF_BADGE[s.confidence]?.cls || ''}`}>
                          {CONF_BADGE[s.confidence]?.label || s.confidence}
                        </span>
                      </label>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-slate-500">Header row:</span>
                        <input type="number" min="1" max="20" value={s.header_row}
                               onChange={(e) => updateSheet(idx, { header_row: parseInt(e.target.value) || 1 })}
                               className="border border-slate-300 rounded px-1 py-0.5 w-14 text-xs"
                               disabled={!s.include} />
                        <span className="text-slate-500 ml-2">Section name:</span>
                        <input value={s.section_title} onChange={(e) => updateSheet(idx, { section_title: e.target.value })}
                               className="border border-slate-300 rounded px-2 py-0.5 text-xs w-40"
                               disabled={!s.include} />
                        {s.include && (
                          <button
                            onClick={() => updateSheet(idx, {
                              header_row: s._suggested_header_row,
                              mapping: { ...s._suggested_mapping },
                            })}
                            data-testid={`wizard-reset-mapping-${idx}`}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-500 hover:text-indigo-700 hover:border-indigo-300 ml-2"
                            title="Restore the auto-detected header row & column mapping"
                          >
                            Reset to suggested
                          </button>
                        )}
                      </div>
                    </div>
                    {s.include && (
                      <div className="px-3 py-2">
                        {/* Detected columns table — shows actual Excel headers */}
                        {s.detected_columns && s.detected_columns.length > 0 ? (
                          <div>
                            <p className="text-[11px] text-slate-500 mb-1 font-display font-bold uppercase tracking-wider">
                              Columns detected in &quot;{s.name}&quot; — assign each to a field:
                            </p>
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="bg-slate-50">
                                  <th className="text-left px-2 py-1 border border-slate-200">Col #</th>
                                  <th className="text-left px-2 py-1 border border-slate-200">Header in Excel</th>
                                  <th className="text-left px-2 py-1 border border-slate-200">Map to field</th>
                                </tr>
                              </thead>
                              <tbody>
                                {s.detected_columns.filter(c => !c.is_blank_header).map((col) => {
                                  // Compute current mapped_to from live mapping state
                                  const currentMapped = Object.entries(s.mapping || {}).find(([, v]) => v === col.col_index)?.[0] || '__ignore__';
                                  return (
                                    <tr key={col.col_index} className={currentMapped !== '__ignore__' ? 'bg-emerald-50' : ''}>
                                      <td className="px-2 py-1 border border-slate-200 text-slate-400">{col.col_index}</td>
                                      <td className="px-2 py-1 border border-slate-200 font-mono">{col.header_text}</td>
                                      <td className="px-2 py-1 border border-slate-200">
                                        <select
                                          value={currentMapped}
                                          onChange={(e) => updateSheetMapping(s.name, col.col_index, e.target.value)}
                                          className="text-xs border border-slate-200 rounded px-1 py-0.5 w-full bg-white"
                                        >
                                          <option value="__ignore__">— ignore —</option>
                                          <option value="description">Description (required)</option>
                                          <option value="item_code">Item Code / Sr No</option>
                                          <option value="unit">Unit (UOM)</option>
                                          <option value="qty">Quantity</option>
                                          <option value="rate">Rate</option>
                                          <option value="amount">Amount</option>
                                        </select>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          // Fallback: legacy manual column picker grid
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {CONCEPTS.map((c) => (
                              <label key={c.key} className="flex items-center gap-1.5">
                                <span className="text-slate-600 w-20">{c.label}{c.required && <span className="text-rose-600">*</span>}:</span>
                                <select value={s.mapping[c.key] ?? ''}
                                        onChange={(e) => updateMapping(idx, c.key, e.target.value)}
                                        data-testid={`wizard-map-${idx}-${c.key}`}
                                        className="border border-slate-300 rounded px-1 py-0.5 text-xs bg-white flex-1">
                                  <option value="">—</option>
                                  {Array.from({ length: Math.min(s.cols, 60) }, (_, i) => i + 1).map((n) => (
                                    <option key={n} value={n}>{`Col ${colLetter(n)} (${n})`}</option>
                                  ))}
                                </select>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {s.include && s.preview && (
                      <details className="px-3 pb-2">
                        <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-700">
                          <Eye size={11} className="inline mb-0.5" /> Preview first rows
                        </summary>
                        <div className="overflow-x-auto mt-2 border border-slate-200 rounded">
                          <table className="w-full text-[10px]">
                            <tbody>
                              {s.preview.slice(0, 8).map((row, ri) => (
                                <tr key={ri} className={ri + 1 === s.header_row ? 'bg-amber-50 font-semibold' : ''}>
                                  <td className="px-1 py-0.5 text-slate-400 border-r border-slate-100">{ri + 1}</td>
                                  {row.slice(0, 12).map((c, ci) => (
                                    <td key={ci} className="px-1 py-0.5 truncate max-w-[120px] border-r border-slate-100">{c ?? ''}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && preview && (
            <div data-testid="wizard-step-preview">
              <h3 className="font-display font-bold text-base text-slate-800 mb-3">Preview & confirm import</h3>

              {(preview.warnings || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded p-3 mb-4 space-y-2" data-testid="wizard-warnings-banner">
                  {(preview.warnings || []).map((w, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 text-xs">
                      <div className="flex items-start gap-2">
                        <AlertCircle size={14} className="text-amber-700 mt-0.5 flex-shrink-0" />
                        <span className="text-amber-900">{w.message}</span>
                      </div>
                      <button onClick={() => excludeSheetAndRepreview(w.sheet)} disabled={busy}
                              data-testid={`wizard-exclude-sheet-${i}`}
                              className="flex-shrink-0 px-2 py-1 text-[11px] rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 font-semibold whitespace-nowrap">
                        Exclude this sheet & re-preview
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Items</div>
                  <div className="text-lg font-bold tabular-nums">{preview.total_items}</div>
                </div>
                <div className="rounded border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Value</div>
                  <div className="text-lg font-bold tabular-nums">
                    ₹{(preview.total_value / 1e7).toFixed(2)} Cr
                  </div>
                </div>
                <div className="rounded border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Sections</div>
                  <div className="text-lg font-bold tabular-nums">{(preview.sections || []).length}</div>
                </div>
                <div className="rounded border p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Skipped rows</div>
                  <div className="text-lg font-bold tabular-nums">{preview.skipped_rows || 0}</div>
                </div>
              </div>

              {(preview.sections || []).length > 0 && (
                <div className="border rounded overflow-hidden mb-4" data-testid="wizard-section-breakdown">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="text-left px-2 py-1.5">Section (sheet)</th>
                        <th className="text-right px-2 py-1.5">Items</th>
                        <th className="text-right px-2 py-1.5">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.sections || []).map((s, i) => {
                        const zero = (s.value || 0) <= 0;
                        return (
                          <tr key={i} className={`border-t ${zero ? 'bg-amber-50' : ''}`}>
                            <td className="px-2 py-1">{s.section}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{s.items}</td>
                            <td className="px-2 py-1 text-right tabular-nums">
                              {inr(s.value)}
                              {zero && <span className="ml-2 text-[10px] text-amber-800 font-semibold">⚠ likely not BOQ data</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="border rounded overflow-hidden mb-4">
                <table className="w-full text-xs" data-testid="wizard-preview-table">
                  <thead className="bg-slate-50">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="text-left px-2 py-1.5">Code</th>
                      <th className="text-left px-2 py-1.5">Description</th>
                      <th className="text-left px-2 py-1.5">Unit</th>
                      <th className="text-right px-2 py-1.5">Qty</th>
                      <th className="text-right px-2 py-1.5">Rate</th>
                      <th className="text-right px-2 py-1.5">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.preview_items || []).map((it, i) => (
                      <tr key={i} className={`border-t ${it.is_group ? 'bg-indigo-50/30 font-semibold' : ''}`}>
                        <td className="px-2 py-1 text-slate-600">{it.item_code || '—'}</td>
                        <td className="px-2 py-1 truncate max-w-md">
                          {it.description}
                          {it.rate_basis && (
                            <span className="ml-1 text-[10px] px-1 rounded bg-amber-100 text-amber-800">{it.rate_basis}</span>
                          )}
                        </td>
                        <td className="px-2 py-1">{it.unit || '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{(it.quantity || 0).toLocaleString('en-IN')}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{it.rate ?? '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{(it.amount || 0).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.total_items > (preview.preview_items || []).length && (
                <p className="text-[11px] text-slate-500 italic mb-3" data-testid="wizard-preview-caption">
                  Showing priced items ({preview.leaf_count ?? preview.total_items} priced/leaf items,{' '}
                  {preview.group_count ?? 0} section/group rows not shown) · first {(preview.preview_items || []).length} of {preview.total_items} total rows.
                </p>
              )}

              {hasExistingBoq && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs flex items-start gap-2">
                  <AlertCircle size={14} className="text-amber-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" className="accent-amber-600" checked={replace}
                             onChange={(e) => setReplace(e.target.checked)} data-testid="wizard-replace-toggle" />
                      <span className="font-semibold">Replace existing BOQ for this project</span>
                    </label>
                    {replace && (
                      <label className="block mt-2">
                        <input type="checkbox" className="accent-rose-600 mr-1.5" checked={confirmReplace}
                               onChange={(e) => setConfirmReplace(e.target.checked)} data-testid="wizard-confirm-replace" />
                        <span className="text-rose-700">I understand the existing BOQ will be wiped and replaced with this file.</span>
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-3 flex justify-between items-center bg-slate-50">
          <button onClick={() => step > 1 && setStep(step - 1)} disabled={step === 1 || busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-slate-700 hover:text-slate-900 disabled:opacity-30">
            <ArrowLeft size={12} /> Back
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
                    className="px-3 py-1.5 text-xs text-slate-700 hover:text-slate-900">Cancel</button>
            {step === 2 && (
              <button onClick={() => goPreview()} disabled={busy}
                      data-testid="wizard-next-preview-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] font-display font-semibold">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview import
              </button>
            )}
            {step === 3 && (
              <button onClick={commit} disabled={busy || (replace && !confirmReplace)}
                      data-testid="wizard-confirm-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-700 text-white rounded-sm hover:bg-emerald-800 font-display font-semibold disabled:opacity-50">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                Confirm & import
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
